<template>
  <div>
    <section class="bridge-card">
      <header class="bridge-header">
        <div>
          <h2 class="bridge-title">Features</h2>
          <p class="bridge-description">
            Mirrors Stripe Entitlements features. Changes are written to Stripe, then re-mirrored to Postgres automatically.
          </p>
        </div>
        <div class="bridge-toolbar">
          <aha-status-badge v-if="mode" :look="isLive ? 'alert' : 'quiet'" :aria-label="modeLabel + ' mode'">{{ modeLabel }} mode</aha-status-badge>
          <label class="bridge-switch">
            <aha-switch
              size="small"
              aria-label="Show archived"
              :checked.prop="includeArchived"
              :disabled.prop="isLoading || !!busyKey"
              @change="toggleArchived"
            ></aha-switch>
            Show archived
          </label>
          <aha-button size="sm" :disabled="isLoading || !!busyKey" :loading="isLoading" @click="loadCatalog">Reload</aha-button>
        </div>
      </header>

      <aha-alert v-if="notice" type="warning" closable class="bridge-block" @close="notice = ''">{{ notice }}</aha-alert>
      <p v-if="isLoading && !loaded" class="bridge-muted" role="status">Loading the catalogue from Stripe</p>
      <aha-alert v-else-if="loadError" type="error" class="bridge-block">
        {{ loadError }}
        <aha-button slot="action" size="sm" @click="loadCatalog">Try again</aha-button>
      </aha-alert>
      <template v-else-if="loaded">
        <form class="bridge-block bridge-create" novalidate @submit.prevent="askCreate">
          <h3 class="bridge-subtitle">Create a feature</h3>
          <p class="bridge-muted">Creates the feature in Stripe, so it also appears in the Stripe dashboard, then mirrors it to Postgres.</p>
          <div class="bridge-create-fields">
            <div class="bridge-field">
              <span class="bridge-label">Name</span>
              <aha-input
                aria-label="Name"
                placeholder="For example, Audience size 50"
                maxlength="80"
                :value="newFeature.name"
                :disabled.prop="!!busyKey"
                @input="newFeature.name = detailValue($event, newFeature.name)"
              ></aha-input>
            </div>
            <div class="bridge-field">
              <span class="bridge-label">Lookup key</span>
              <aha-input
                aria-label="Lookup key"
                placeholder="category.key"
                maxlength="80"
                :value="newFeature.lookupKey"
                :status="lookupKeyError ? 'error' : 'default'"
                :error-message="lookupKeyError"
                :info-message="lookupKeyError ? '' : lookupKeyHint"
                :disabled.prop="!!busyKey"
                @input="newFeature.lookupKey = detailValue($event, newFeature.lookupKey)"
              ></aha-input>
            </div>
            <aha-button variant="secondary" :disabled="!canCreate || !!busyKey" @click="askCreate">
              <span slot="icon"><aha-icon name="system-plus" size="16" decorative></aha-icon></span>
              Create feature
            </aha-button>
          </div>
        </form>

        <h3 class="bridge-subtitle">All features ({{ features.length }})</h3>
        <p v-if="!features.length" class="bridge-muted">Stripe returned no features.</p>
        <div v-else class="bridge-table-wrap">
          <table class="bridge-table">
            <thead>
              <tr><th>Feature</th><th>Attached to</th><th>Actions</th></tr>
            </thead>
            <tbody>
              <tr v-for="feature in features" :key="feature.id">
                <td>
                  <span class="bridge-cell-strong">{{ feature.name }}</span>
                  <aha-status-badge v-if="!feature.active" size="sm" look="quiet">Archived</aha-status-badge>
                  <span class="bridge-cell-sub"><code>{{ feature.lookupKey }}</code></span>
                </td>
                <td>{{ attachedProductIds(feature.id).length }} products</td>
                <td>
                  <div class="bridge-row-actions">
                    <aha-button size="sm" variant="secondary" :disabled="!!busyKey" @click="openEdit(feature)">Edit</aha-button>
                    <aha-button
                      v-if="feature.active"
                      size="sm"
                      variant="danger"
                      :disabled="!!busyKey"
                      @click="openArchive(feature)"
                    >Archive</aha-button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </section>

    <stripe-bridge-confirm
      :open="dialog === 'create'"
      title="Create this feature?"
      ok-label="Create"
      :busy="busyKey === 'create'"
      :error="dialogError"
      @cancel="closeDialog"
      @confirm="runCreate"
    >
      <p>
        Create feature <strong>{{ newFeature.name.trim() }}</strong> with lookup key <code>{{ newFeature.lookupKey.trim() }}</code> in Stripe
        <aha-status-badge size="sm" :look="isLive ? 'alert' : 'quiet'">{{ modeLabel }}</aha-status-badge>?
      </p>
      <p class="bridge-muted">The feature is then mirrored to Postgres.</p>
    </stripe-bridge-confirm>

    <stripe-bridge-dialog :open="dialog === 'edit'" :title="editing ? 'Edit ' + editing.name : 'Edit feature'" :busy="!!busyKey" @close="closeDialog">
      <template v-if="editing">
        <div class="bridge-field">
          <span class="bridge-label">Name</span>
          <aha-input
            aria-label="Feature name"
            maxlength="80"
            :value="editName"
            :disabled.prop="!!busyKey"
            @input="editName = detailValue($event, editName)"
          ></aha-input>
        </div>
        <div class="bridge-field">
          <span class="bridge-label">Lookup key</span>
          <aha-input aria-label="Lookup key" :value="editing.lookupKey" :disabled.prop="true"></aha-input>
          <span class="bridge-muted">Stripe does not allow changing a lookup key.</span>
        </div>
        <div>
          <aha-button variant="secondary" :disabled="!canSaveName || !!busyKey" :loading="busyKey === 'rename'" @click="saveName">Save name</aha-button>
        </div>
        <aha-alert v-if="dialogError" type="error" size="small">{{ dialogError }}</aha-alert>
        <stripe-bridge-link-editor
          heading="Products with this feature"
          empty-text="This feature is not attached to any product."
          pick-label="Attach to products"
          picker-label="Products"
          placeholder="Search by product name or id"
          :options="productOptions"
          :attached-ids="attachedProductIds(editing.id)"
          :busy="busyKey === 'link'"
          :errors="linkErrors"
          :message="linkMessage"
          @attach="attachProducts"
          @detach="detachProduct"
        ></stripe-bridge-link-editor>
      </template>
      <template slot="footer">
        <aha-button variant="secondary" :disabled="!!busyKey" @click="closeDialog">Done</aha-button>
      </template>
    </stripe-bridge-dialog>

    <stripe-bridge-confirm
      :open="dialog === 'archive'"
      title="Archive this feature?"
      ok-label="Archive feature"
      danger
      :busy="busyKey === 'archive'"
      :error="dialogError"
      :require-text="isLive && archiving ? archiving.name : ''"
      @cancel="closeDialog"
      @confirm="runArchive"
    >
      <template v-if="archiving">
        <aha-alert type="warning" size="small">
          Archiving is permanent in Stripe: there is no delete, and an archived feature cannot be attached to new products.
        </aha-alert>
        <p>
          Archive <strong>{{ archiving.name }}</strong> (<code>{{ archiving.lookupKey }}</code>) in Stripe
          <aha-status-badge size="sm" :look="isLive ? 'alert' : 'quiet'">{{ modeLabel }}</aha-status-badge>?
        </p>
        <template v-if="archiveProducts.length">
          <p>It is attached to {{ archiveProducts.length }} {{ archiveProducts.length === 1 ? 'product' : 'products' }} in the list:</p>
          <ul class="archive-products">
            <li v-for="product in archiveProducts" :key="product.id">{{ product.name }} <code>{{ product.id }}</code></li>
          </ul>
        </template>
        <label class="archive-check">
          <input v-model="detachOnArchive" type="checkbox" :disabled="busyKey === 'archive'" />
          Also detach it from every product it is attached to, including archived products
        </label>
      </template>
    </stripe-bridge-confirm>

    <section class="bridge-card">
      <h2 class="bridge-title">Bulk attach</h2>
      <p class="bridge-description">Attach several features or products at once, starting from a feature or from a product.</p>
      <stripe-bridge-attach v-if="loaded" :key="attachKey" hide-create :shared-catalog="sharedCatalog" @changed="onAttachChanged" />
    </section>
  </div>
</template>

<script>
import Config from '@/config'
import utils from '@/utils'
import catalogMixin, { errorMessage } from './catalogMixin'
import nativeAhaElements from './nativeAhaElements'
import StripeBridgeAttach from './StripeBridgeAttach'
import StripeBridgeConfirm from './StripeBridgeConfirm'
import StripeBridgeDialog from './StripeBridgeDialog'
import StripeBridgeLinkEditor from './StripeBridgeLinkEditor'

const MAX_STRIPE_TEXT_LENGTH = 80
const LOOKUP_KEY_PATTERN = /^[a-z0-9._-]{1,80}$/

export default {
  name: 'StripeBridgeFeatures',
  components: { ...nativeAhaElements, StripeBridgeAttach, StripeBridgeConfirm, StripeBridgeDialog, StripeBridgeLinkEditor },
  mixins: [catalogMixin],
  data() {
    return {
      dialog: '',
      dialogError: '',
      newFeature: { name: '', lookupKey: '' },
      lookupKeyHint: 'Lowercase letters, digits, full stops, underscores and hyphens, up to 80 characters. Use the category.key style.',
      editing: null,
      editName: '',
      linkErrors: {},
      linkMessage: null,
      archiving: null,
      detachOnArchive: true,
      attachKey: 0,
    }
  },
  computed: {
    trimmedName() {
      return this.newFeature.name.trim()
    },
    trimmedLookupKey() {
      return this.newFeature.lookupKey.trim()
    },
    lookupKeyError() {
      return this.trimmedLookupKey && !LOOKUP_KEY_PATTERN.test(this.trimmedLookupKey)
        ? 'Use lowercase letters, digits, full stops, underscores and hyphens only, up to 80 characters.'
        : ''
    },
    canCreate() {
      return !!this.trimmedName && this.trimmedName.length <= MAX_STRIPE_TEXT_LENGTH && !!this.trimmedLookupKey && !this.lookupKeyError
    },
    canSaveName() {
      const name = this.editName.trim()
      return !!this.editing && !!name && name.length <= MAX_STRIPE_TEXT_LENGTH && name !== this.editing.name
    },
    productOptions() {
      return this.products.map(p => ({ id: p.id, label: p.name, sub: p.id, active: p.active }))
    },
    sharedCatalog() {
      return { mode: this.mode, features: this.features, products: this.products, attachments: this.attachments }
    },
    archiveProducts() {
      return this.archiving ? this.attachedProductIds(this.archiving.id).map(id => this.productById[id]).filter(Boolean) : []
    },
  },
  methods: {
    closeDialog() {
      if (this.busyKey) return
      this.dialog = ''
      this.dialogError = ''
    },
    onAttachChanged() {
      this.loadCatalog()
    },
    askCreate() {
      if (!this.canCreate || this.busyKey) return
      this.dialogError = ''
      this.dialog = 'create'
    },
    async runCreate() {
      this.busyKey = 'create'
      this.dialogError = ''
      try {
        const res = await this.$http.post(Config.api.stripeMirrorFeatures, { name: this.trimmedName, lookupKey: this.trimmedLookupKey })
        const created = res.body.feature
        this.features = [...this.features, created]
        this.newFeature = { name: '', lookupKey: '' }
        this.attachKey += 1
        this.busyKey = ''
        this.dialog = ''
        if (res.body.mirrored === false) this.notice = this.unmirroredNotice([created.name], 'created')
        else utils.showToastSuccess(`${created.name} created in Stripe and mirrored.`)
      } catch (error) {
        this.dialogError = errorMessage(error, 'Could not create the feature. Please try again.')
      } finally {
        this.busyKey = ''
      }
    },
    openEdit(feature) {
      this.editing = feature
      this.editName = feature.name
      this.linkErrors = {}
      this.linkMessage = null
      this.dialogError = ''
      this.dialog = 'edit'
    },
    async saveName() {
      if (!this.canSaveName || this.busyKey) return
      this.busyKey = 'rename'
      this.dialogError = ''
      try {
        const res = await this.$http.patch(`${Config.api.stripeMirrorFeatures}/${encodeURIComponent(this.editing.id)}`, { name: this.editName.trim() })
        const updated = (res.body && res.body.feature) || { ...this.editing, name: this.editName.trim() }
        this.features = this.features.map(f => (f.id === updated.id ? { ...f, ...updated } : f))
        this.editing = this.featureById[updated.id]
        this.editName = this.editing.name
        if (res.body && res.body.mirrored === false) this.notice = this.unmirroredNotice([this.editing.name])
        else utils.showToastSuccess('Feature name updated in Stripe and mirrored.')
        this.attachKey += 1
      } catch (error) {
        this.dialogError = errorMessage(error, 'Could not save the name. Please try again.')
      } finally {
        this.busyKey = ''
      }
    },
    async attachProducts(productIds) {
      if (this.busyKey || !productIds.length) return
      this.busyKey = 'link'
      this.linkErrors = {}
      this.linkMessage = null
      const done = []
      const unmirrored = []
      const errors = {}
      for (const productId of productIds) {
        try {
          const body = await this.attachPair(productId, this.editing.id)
          const name = this.productById[productId].name
          done.push(name)
          if (body && body.mirrored === false) unmirrored.push(name)
        } catch (error) {
          errors[productId] = errorMessage(error, 'Could not attach. Please try again.')
        }
      }
      this.linkErrors = errors
      this.busyKey = ''
      const failed = Object.keys(errors).length
      if (done.length) this.linkMessage = { type: failed ? 'warning' : 'success', text: `Attached ${this.editing.name} to ${done.join(', ')}.${failed ? ` ${failed} failed.` : ''}` }
      else if (failed) this.linkMessage = { type: 'error', text: 'Nothing was attached. See the errors in the list.' }
      if (unmirrored.length) this.notice = this.unmirroredNotice(unmirrored)
      this.attachKey += 1
    },
    async detachProduct(productId) {
      if (this.busyKey) return
      this.busyKey = 'link'
      this.linkMessage = null
      try {
        const body = await this.detachPair(productId, this.editing.id)
        if (body && body.mirrored === false) this.notice = this.unmirroredNotice([this.productById[productId].name])
        else utils.showToastSuccess('Detached. Mirror updated.')
        this.attachKey += 1
      } catch (error) {
        this.linkMessage = { type: 'error', text: errorMessage(error, 'Could not detach. Please try again.') }
      } finally {
        this.busyKey = ''
      }
    },
    openArchive(feature) {
      this.archiving = feature
      this.detachOnArchive = true
      this.dialogError = ''
      this.dialog = 'archive'
    },
    async runArchive() {
      if (this.busyKey) return
      this.busyKey = 'archive'
      this.dialogError = ''
      const feature = this.archiving
      try {
        const url = `${Config.api.stripeMirrorFeatures}/${encodeURIComponent(feature.id)}/archive`
        const res = await this.$http.post(url, { detachFromProducts: this.detachOnArchive })
        this.busyKey = ''
        this.dialog = ''
        const mirrorProblem = res.body && (res.body.mirrorError || (res.body.mirrorErrors && res.body.mirrorErrors.length) || res.body.mirrored === false)
        if (mirrorProblem) this.notice = `${feature.name} archived in Stripe, but the mirror was not fully updated. Run "Sync".`
        else utils.showToastSuccess(`${feature.name} archived in Stripe.`)
        this.attachKey += 1
        await this.loadCatalog()
      } catch (error) {
        this.dialogError = errorMessage(error, 'Could not archive the feature. Please try again.')
        this.attachKey += 1
        await this.loadCatalog()
      } finally {
        this.busyKey = ''
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import './bridgeStyles.scss';

.archive-products {
  margin: 0;
  padding-left: var(--aha-space-20, 20px);
}

.archive-check {
  display: flex;
  align-items: center;
  gap: var(--aha-space-8);
  margin: 0;
}
</style>
